// Centralized debug logging system
interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  category: string;
  message: string;
  data?: any;
  authId?: string;
}

class DebugLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private isEnabled = true;

  private createLogEntry(
    level: LogEntry['level'],
    category: string,
    message: string,
    data?: any,
    authId?: string
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      authId
    };
  }

  private addLog(entry: LogEntry) {
    if (!this.isEnabled) return;

    this.logs.push(entry);
    
    // Trim logs if exceeding max
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output with appropriate level
    const prefix = `[${entry.category}]`;
    const message = `${prefix} ${entry.message}`;
    
    switch (entry.level) {
      case 'error':
        console.error(message, entry.data || '');
        break;
      case 'warn':
        console.warn(message, entry.data || '');
        break;
      case 'debug':
        console.debug(message, entry.data || '');
        break;
      default:
        console.log(message, entry.data || '');
    }
  }

  // Public logging methods
  info(category: string, message: string, data?: any, authId?: string) {
    this.addLog(this.createLogEntry('info', category, message, data, authId));
  }

  warn(category: string, message: string, data?: any, authId?: string) {
    this.addLog(this.createLogEntry('warn', category, message, data, authId));
  }

  error(category: string, message: string, data?: any, authId?: string) {
    this.addLog(this.createLogEntry('error', category, message, data, authId));
  }

  debug(category: string, message: string, data?: any, authId?: string) {
    this.addLog(this.createLogEntry('debug', category, message, data, authId));
  }

  // Specialized logging methods for common scenarios
  connectionEvent(event: string, status: boolean, authId?: string, extra?: any) {
    this.info('CONNECTION', `${event}: ${status}`, { status, ...extra }, authId);
  }

  groupsEvent(event: string, count?: number, authId?: string, extra?: any) {
    this.info('GROUPS', event, { count, ...extra }, authId);
  }

  pollingEvent(event: string, authId?: string, extra?: any) {
    this.debug('POLLING', event, extra, authId);
  }

  uiEvent(event: string, component: string, extra?: any) {
    this.debug('UI', `${component}: ${event}`, extra);
  }

  // Utility methods
  exportLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    console.log('[DEBUG] Logs cleared');
  }

  getLogsSince(timestamp: string): LogEntry[] {
    return this.logs.filter(log => log.timestamp >= timestamp);
  }

  getLogsByCategory(category: string): LogEntry[] {
    return this.logs.filter(log => log.category === category);
  }

  getRecentErrors(minutes: number = 5): LogEntry[] {
    const cutoff = new Date(Date.now() - minutes * 60000).toISOString();
    return this.logs.filter(log => 
      log.level === 'error' && log.timestamp >= cutoff
    );
  }

  // Configuration
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    console.log(`[DEBUG] Logging ${enabled ? 'enabled' : 'disabled'}`);
  }

  setMaxLogs(max: number) {
    this.maxLogs = max;
    if (this.logs.length > max) {
      this.logs = this.logs.slice(-max);
    }
  }
}

// Export singleton instance
export const debugLogger = new DebugLogger();

// Make it globally available for debugging
if (typeof window !== 'undefined') {
  (window as any).debugLogger = debugLogger;
}